import { createPortal } from 'react-dom';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';

const DROPDOWN_WIDTH = 198;
const DROPDOWN_OFFSET = 8;
const VIEWPORT_PADDING = 8;

export default function TagColorPicker({
    options = [],
    backgroundColor,
    textColor,
    onChange,
    label = '',
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState(null);
    const containerRef = useRef(null);
    const buttonRef = useRef(null);
    const dropdownRef = useRef(null);

    const closeDropdown = useCallback(() => {
        setIsOpen(false);
        setPosition(null);
    }, []);

    const updateDropdownPosition = useCallback(() => {
        const button = buttonRef.current;

        if (!button) {
            return;
        }

        const rect = button.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const dropdownWidth = Math.min(
            DROPDOWN_WIDTH,
            viewportWidth - (VIEWPORT_PADDING * 2)
        );
        const dropdownHeight = dropdownRef.current?.offsetHeight ?? 0;
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;
        const shouldOpenUp = dropdownHeight > 0
            && spaceBelow < dropdownHeight + DROPDOWN_OFFSET
            && spaceAbove > spaceBelow;
        const rawLeft = rect.left;
        const left = Math.min(
            Math.max(rawLeft, VIEWPORT_PADDING),
            viewportWidth - dropdownWidth - VIEWPORT_PADDING
        );
        const rawTop = shouldOpenUp
            ? rect.top - dropdownHeight - DROPDOWN_OFFSET
            : rect.bottom + DROPDOWN_OFFSET;

        setPosition({
            top: Math.max(rawTop, VIEWPORT_PADDING),
            left,
            width: dropdownWidth,
        });
    }, []);

    useLayoutEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        updateDropdownPosition();

        const handleClickOutside = (event) => {
            const clickedContainer = containerRef.current?.contains(event.target);
            const clickedDropdown = dropdownRef.current?.contains(event.target);

            if (!clickedContainer && !clickedDropdown) {
                closeDropdown();
            }
        };

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                closeDropdown();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);
        window.addEventListener('resize', updateDropdownPosition);
        window.addEventListener('scroll', updateDropdownPosition, true);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
            window.removeEventListener('resize', updateDropdownPosition);
            window.removeEventListener('scroll', updateDropdownPosition, true);
        };
    }, [isOpen, closeDropdown, updateDropdownPosition]);

    const handleSelect = (option) => {
        onChange?.(option);
        closeDropdown();
    };

    const toggleDropdown = () => {
        if (isOpen) {
            closeDropdown();
            return;
        }

        updateDropdownPosition();
        setIsOpen(true);
    };

    return (
        <div ref={containerRef} className="inline-flex items-center gap-1">
            <span>{label}</span>

            <div className="relative">
                <button
                    ref={buttonRef}
                    type="button"
                    className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded border border-black/10 font-semibold leading-none transition-transform duration-150 ease-out hover:scale-[1.02] focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-400 focus-visible:ring-offset-1"
                    style={{ backgroundColor, color: textColor }}
                    onClick={toggleDropdown}
                    aria-expanded={isOpen}
                    aria-haspopup="listbox"
                    aria-label={`${label} Abrir opções de cores`}
                >
                    A
                </button>

                {isOpen && position && createPortal(
                    <div
                        ref={dropdownRef}
                        className="fixed z-[9999] overflow-visible rounded-md border border-gray-200 bg-white p-3 shadow-lg"
                        style={{
                            top: position.top,
                            left: position.left,
                            width: position.width,
                        }}
                        role="listbox"
                        aria-label="Opções de cores do marcador"
                        onClick={(event) => event.stopPropagation()}
                        onMouseDown={(event) => event.stopPropagation()}
                    >
                        <div className="grid grid-cols-[repeat(5,1.75rem)] gap-2">
                            {options.map((option) => {
                                const isSelected = option.background_color === backgroundColor
                                    && option.text_color === textColor;

                                return (
                                    <button
                                        key={option.id}
                                        type="button"
                                        className="relative flex size-7 shrink-0 cursor-pointer items-center justify-center rounded border font-semibold leading-none transition-transform duration-150 ease-out hover:scale-[1.02] focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
                                        style={{
                                            backgroundColor: option.background_color,
                                            color: option.text_color,
                                        }}
                                        onClick={() => handleSelect(option)}
                                        role="option"
                                        aria-selected={isSelected}
                                        aria-label={`Selecionar cor ${option.id}${isSelected ? ', selecionada' : ''}`}
                                    >
                                        A
                                    </button>
                                );
                            })}
                        </div>
                    </div>,
                    document.body
                )}
            </div>
        </div>
    );
}
